// BUILD-AZONOSÍTÓ — melyik commit webbuildje fut ÉPPEN ezen a kliensen.
//
// MIÉRT (2026-10-06, a fejlesztési ciklus-lap végrehajtási köre):
// az `ios/App/App/public` GITIGNORE-OLT, tehát a natív appba becsomagolt
// webréteg SOHA nincs a verziókövetésben. Az 1.0 (18)-nál ez odáig vezetett,
// hogy a becsomagolt kliens (0f6f896, 09-17) és a natív burkolat (594033b,
// 09-22) öt napra szétcsúszott, és ezt EGYEDÜL egy commit-üzenet prózája
// rögzítette. Egy Store-ból telepített usernél a kliens-réteg a beküldés
// napján befagy — ha nem tudjuk, MELYIK napon, akkor azt sem tudjuk, mi védi
// őket (lásd CLAUDE.md „Aktuális kontextus": ott a firestore.rules esetenként
// az EGYETLEN réteg).
//
// A build-lépés ezért beírja a commitot magába a bundle-be, az app pedig
// megjeleníti. Ebből lesz a `git log ios/1.0-<build>-web..main` horgony is.
//
// NINCS BENNE AZONOSÍTÓ: commit-hash, időpont, mód — a CLAUDE.md 1. szabálya
// szerint ez nem felhasználói adat, telemetriába sem megy, csak megjelenik.

/** A build-lépés által beírt nyers értékek (CRA: process.env.REACT_APP_*). */
export type BuildEnv = {
  commit?: string;
  builtAt?: string;
  mode?: string;
  dirty?: string;
};

export type BuildInfo = {
  /** Rövid commit-hash, vagy null, ha a build-lépés nem írta be. */
  commit: string | null;
  /** A build időpontja, ahogy a build-lépés beírta, vagy null. */
  builtAt: string | null;
  /** "web" | "native" | null — melyik build-mód állította elő. */
  mode: "web" | "native" | null;
  /** Igaz, ha a build commitolatlan munkafából készült. */
  dirty: boolean;
  /** Igaz, ha nincs build-azonosító (pl. `npm start` fejlesztői szerver). */
  isDev: boolean;
};

/** A hash megjelenített hossza. Hét karakter a git saját rövidítése. */
export const SHORT_HASH_LENGTH = 7;

/**
 * Tiszta leképezés — a nyers env-ből tipizált build-információ.
 *
 * ⚠️ Az üres string NEM hiányzó érték helyett szerepel: a CRA a nem
 * definiált `REACT_APP_*`-ot üres stringként inlineolja, tehát a
 * `commit === ""` a NORMÁL eset fejlesztői szerveren, nem hiba.
 */
export function resolveBuildInfo(env: BuildEnv): BuildInfo {
  const commit = (env.commit || "").trim();
  const builtAt = (env.builtAt || "").trim();
  const rawMode = (env.mode || "").trim();
  const mode = rawMode === "web" || rawMode === "native" ? rawMode : null;

  return {
    commit: commit ? commit.slice(0, SHORT_HASH_LENGTH) : null,
    builtAt: builtAt || null,
    mode,
    // Csak az explicit "1" jelent szennyezett fát. Bármi más (üres, "0",
    // hiányzó) tiszta — a "dirty" HAMIS pozitívja riasztás-szemantika
    // lenne ott, ahol nincs mit riasztani.
    dirty: (env.dirty || "").trim() === "1",
    isDev: !commit,
  };
}

/**
 * Megjelenítendő címke. EGYETLEN kanonikus forrás (CLAUDE.md 10. szabály) —
 * a Profil képernyő ezt hívja, más helyen ne fogalmazzuk újra.
 *
 * Fejlesztői szerveren szándékosan NEM hazudik verziót: kimondja, hogy
 * fejlesztői build. Egy kitalált hash rosszabb, mint a hiánya — abból
 * utólag nem derül ki, hogy nem tudjuk.
 */
export function formatBuildLabel(info: BuildInfo): string {
  if (info.isDev) return "fejlesztői build";

  const parts: string[] = [info.commit as string];
  if (info.builtAt) parts.push(info.builtAt);
  if (info.mode === "native") parts.push("natív");
  if (info.dirty) parts.push("commitolatlan");
  return parts.join(" · ");
}

/** Az éppen futó kliens build-információja. */
export function currentBuildInfo(): BuildInfo {
  return resolveBuildInfo({
    commit: process.env.REACT_APP_BUILD_COMMIT,
    builtAt: process.env.REACT_APP_BUILD_AT,
    mode: process.env.REACT_APP_BUILD_MODE,
    dirty: process.env.REACT_APP_BUILD_DIRTY,
  });
}
